import type { ReactNode } from "react";
import { useCellContents } from "@/lib/api/map";
import {
  useLocationLps,
  useOrderDetail,
  useSkuNames,
  type PickingOrder,
  type SceneCell,
  type TaskDetail,
} from "@/lib/api/map3d";
import { describeAddress, zoneName } from "@/lib/api/address";
import { qty, relative } from "@/lib/format";
import { roleLabel, statusLabel } from "@/lib/labels";
import type { Placed } from "./geometry";
import { metres, openLine, type PalletInfo, type WorkerInfo } from "./model";

/**
 * Chap tomondagi suzuvchi karta (burchak qavslari bilan): tanlangan
 * katak/pallet yoki xodimning TO'LIQ holati.
 */

export function Card({ kicker, title, onClose, children }: {
  kicker: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <section className="m3d-card m3d-brackets" aria-label={title}>
      <header className="m3d-card-head">
        <div>
          <div className="m3d-kicker">{kicker}</div>
          <div className="m3d-card-title">{title}</div>
        </div>
        <button type="button" className="m3d-icon-btn" onClick={onClose} aria-label="Yopish" title="Yopish">
          ×
        </button>
      </header>
      <div className="m3d-card-body">{children}</div>
    </section>
  );
}

function Fact({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="m3d-fact">
      <span>{label}</span>
      <b className={tone ? `m3d-tone-${tone}` : undefined}>{value}</b>
    </div>
  );
}

const STEP_LABEL: Record<string, string> = {
  SCAN_SOURCE: "Manbani skanerladi",
  SCAN_LP: "LP skanerladi",
  CONFIRM_QTY: "Miqdorni tasdiqladi",
  SCAN_TARGET: "Maqsadni skanerladi",
  CONFIRM_DROP: "Joyiga qo'ydi",
  SELECT_SECTOR: "Sektor tanladi",
  PHOTO: "Surat oldi",
  NOTE: "Izoh yozdi",
  LINE_CONFIRMED: "Satrni yopdi",
  LINE_SHORT: "Kam bilan yopdi",
  LINE_CANCELLED: "Satr bekor qilindi",
};

export function stepLabel(step: string | undefined): string {
  if (!step) return "—";
  return STEP_LABEL[step] ?? statusLabel(step);
}

function cellStateLabel(cell: SceneCell): { text: string; tone?: string } {
  if (!cell.is_active) return { text: "Nofaol", tone: "muted" };
  if (cell.blocked) return { text: "Bloklangan", tone: "danger" };
  if (cell.occupied_slots > 0 || Number(cell.on_hand_qty) > 0) return { text: "Band" };
  return { text: "Bo'sh", tone: "muted" };
}

/** Katak (saqlash palleti) kartasi: LP lar, tovarlar, Nazoratdan masofa. */
export function CellCard({ cell, placed, routeLength, byLp, onClose }: {
  cell: SceneCell;
  placed: Placed;
  routeLength: number | null;
  byLp: Map<string, PickingOrder>;
  onClose: () => void;
}) {
  const lps = useLocationLps(cell.location_id);
  // Bo'sh katakda tarkib so'ralmaydi.
  const stocked = cell.occupied_slots > 0 || Number(cell.on_hand_qty) > 0;
  const contents = useCellContents(stocked ? cell.location_id : null);
  const lpItems = lps.data?.items ?? [];
  const names = useSkuNames(lpItems.flatMap((lp) => lp.contents.map((content) => content.sku_id)));
  const state = cellStateLabel(cell);
  const where = placed.kind === "RACK" ? describeAddress(cell.code) : zoneName(cell.zone_code);

  return (
    <Card kicker={placed.kind === "RACK" ? "Katak" : "Hudud katagi"} title={cell.code} onClose={onClose}>
      <div className="m3d-sub">{where}</div>
      <div className="m3d-facts">
        <Fact label="Holat" value={state.text} tone={state.tone} />
        <Fact label="Vazifa" value={cell.location_function || "—"} />
        <Fact label="Turi" value={cell.cell_type || "—"} />
        <Fact label="Pallet" value={`${cell.occupied_slots}${cell.capacity_pallets != null ? ` / ${cell.capacity_pallets}` : ""}`} />
        <Fact label="Qoldiq" value={qty(cell.on_hand_qty)} />
        {placed.pass && <Fact label="O'tish" value="Ostidan yuriladi" tone="accent" />}
        {routeLength != null && <Fact label="Nazoratdan" value={metres(routeLength)} tone="accent" />}
      </div>

      <div className="m3d-section-title">LP / palletlar</div>
      {lps.isLoading && <div className="m3d-muted">Yuklanmoqda…</div>}
      {!lps.isLoading && lpItems.length === 0 && <div className="m3d-muted">Bu katakda LP yo'q</div>}
      {lpItems.map((lp) => {
        const order = byLp.get(lp.id);
        return (
          <div className="m3d-lp" key={lp.id}>
            <div className="m3d-lp-head">
              <span className="m3d-mono">{lp.lp_code}</span>
              <span className="m3d-pill">{statusLabel(lp.status)}</span>
            </div>
            {order && (
              <div className="m3d-lp-order">
                {order.order_number} · {order.customer_name ?? "mijoz noma'lum"} · {statusLabel(order.status)}
              </div>
            )}
            {lp.contents.map((content) => {
              const sku = names.get(content.sku_id);
              return (
                <div className="m3d-sku" key={`${lp.id}-${content.sku_id}-${content.lot_ref ?? ""}`}>
                  <span className="m3d-mono">{sku?.article ?? content.sku_id.slice(0, 8)}</span>
                  <span className="m3d-sku-name">{sku?.name ?? "—"}</span>
                  <b>{qty(content.qty)}</b>
                </div>
              );
            })}
          </div>
        );
      })}

      {(contents.data?.items.length ?? 0) > 0 && (
        <>
          <div className="m3d-section-title">Tovarlar (jami)</div>
          {contents.data!.items.map((row) => (
            <div className="m3d-sku" key={row.sku_id}>
              <span className="m3d-mono">{row.article}</span>
              <span className="m3d-sku-name">{row.name}</span>
              <b>{qty(row.on_hand_qty)}</b>
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

/** Buyurtma palleti kartasi: LP, buyurtma, mijoz, holat va tarkib. */
export function PalletCard({ info, onClose }: { info: PalletInfo; onClose: () => void }) {
  const order = useOrderDetail(info.order?.id);
  const full = order.data ?? info.order;
  const names = useSkuNames((info.lp?.contents ?? []).map((content) => content.sku_id));
  const tara = full?.taras?.find((item) => item.lp_id === info.lp?.id);

  return (
    <Card kicker="Pallet" title={info.lp?.lp_code ?? info.cell.code} onClose={onClose}>
      <div className="m3d-facts">
        <Fact label="Katak" value={info.cell.code} />
        <Fact label="Hudud" value={zoneName(info.cell.zone_code)} />
        <Fact label="LP holati" value={info.lp ? statusLabel(info.lp.status) : "LP noma'lum"} tone={info.marker.tone === "control" ? "accent" : undefined} />
        <Fact label="Buyurtma" value={full?.order_number ?? "—"} />
        <Fact label="Mijoz" value={full?.customer_name ?? "—"} />
        <Fact label="Buyurtma holati" value={full ? statusLabel(full.status) : "—"} />
        {tara && <Fact label="Pallet №" value={`${tara.seq}${full?.taras ? ` / ${full.taras.length}` : ""}`} />}
        {tara?.delivered_at && <Fact label="Yetkazildi" value={relative(tara.delivered_at)} />}
      </div>
      {!info.lp && (
        <div className="m3d-muted">
          Katakda qoldiq bor, lekin unga bog'langan LP ro'yxatda yo'q (LPsiz qoldiq yoki saqlash palleti).
        </div>
      )}
      {(info.lp?.contents.length ?? 0) > 0 && <div className="m3d-section-title">Tarkib</div>}
      {info.lp?.contents.map((content) => {
        const sku = names.get(content.sku_id);
        return (
          <div className="m3d-sku" key={`${content.sku_id}-${content.lot_ref ?? ""}`}>
            <span className="m3d-mono">{sku?.article ?? content.sku_id.slice(0, 8)}</span>
            <span className="m3d-sku-name">{sku?.name ?? "—"}</span>
            <b>{qty(content.qty)}</b>
          </div>
        );
      })}
    </Card>
  );
}

const LINE_TONE: Record<string, string> = {
  CONFIRMED: "ok",
  SHORT: "danger",
  CANCELLED: "muted",
};

/** Xodim kartasi: kim, oxirgi amal, joriy topshiriq va uning satrlari. */
export function WorkerCard({ info, task, codeById, routeLength, onClose }: {
  info: WorkerInfo;
  task: TaskDetail | undefined;
  codeById: Map<string, string>;
  routeLength: number | null;
  onClose: () => void;
}) {
  const position = info.position;
  const lines = task?.lines ?? [];
  const names = useSkuNames(lines.map((line) => line.sku_id));
  const last = position.last;
  const lastCode = last?.location_id ? codeById.get(last.location_id) : undefined;
  const open = lines.filter((line) => openLine(line.status)).length;

  return (
    <Card kicker={position.kind === "forklift" ? "Kara haydovchisi" : "Xodim"} title={info.name} onClose={onClose}>
      <div className="m3d-sub">{position.roles.map(roleLabel).join(", ") || "rolsiz"}</div>
      <div className="m3d-facts">
        <Fact
          label="Holat"
          value={info.status === "live" ? "Faol" : info.status === "idle" ? "Kutmoqda" : info.status === "stale" ? "Eskirgan" : "Amal yo'q"}
          tone={info.status === "live" ? "ok" : info.status === "stale" || info.status === "none" ? "muted" : "accent"}
        />
        <Fact label="Oxirgi amal" value={stepLabel(position.last_action ?? last?.step)} />
        <Fact label="Qachon" value={relative(position.last_activity_at ?? last?.at)} />
        <Fact label="Joyi" value={lastCode ?? last?.scanned_code ?? (last?.zone_code ? zoneName(last.zone_code) : "noma'lum")} />
      </div>

      <div className="m3d-section-title">Joriy topshiriq</div>
      {!position.current && <div className="m3d-muted">Ochiq topshiriq yo'q</div>}
      {position.current && (
        <div className="m3d-facts">
          <Fact label="Turi" value={statusLabel(position.current.task_type)} />
          <Fact label="Holati" value={statusLabel(task?.status ?? position.current.status)} />
          <Fact label="Hujjat" value={task?.document_number ?? position.current.document_number ?? "—"} />
          <Fact label="Satrlar" value={`${lines.length - open} / ${lines.length} bajarildi`} />
          {routeLength != null && <Fact label="Qolgan yo'l" value={metres(routeLength)} tone="accent" />}
        </div>
      )}
      {lines.length > 0 && (
        <div className="m3d-lines">
          {lines.map((line) => {
            const sku = names.get(line.sku_id);
            const source = line.source_location_id ? codeById.get(line.source_location_id) : undefined;
            const tone = LINE_TONE[line.status] ?? "open";
            return (
              <div className={`m3d-line m3d-line-${tone}`} key={line.id}>
                <span className="m3d-dot" />
                <div className="m3d-line-main">
                  <div className="m3d-line-top">
                    <span className="m3d-mono">{sku?.article ?? line.sku_id.slice(0, 8)}</span>
                    <span className="m3d-mono m3d-muted">{source ?? "—"}</span>
                  </div>
                  <div className="m3d-sku-name">{sku?.name ?? "—"}</div>
                </div>
                <div className="m3d-line-qty">
                  <b>{qty(line.actual_qty ?? "0")}/{qty(line.planned_qty)}</b>
                  <span>{statusLabel(line.status)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
